import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { ChevronLeft, ExpandMore } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useApiClient } from "../../../api";
import { InformationSnippet } from "../../../shared-components/InformationSnippet";
import { formatDate } from "../../../utils/formatters";
import { withPageTracking } from "../../../services/postHog";
import { AssignmentUploadDialog } from "./AssignmentUploadDialog";
import { AssignmentGroupUploadDialog } from "./AssignmentGroupUploadDialog";
import { SubmissionSection } from "./SubmissionSection";
import StudentSubmissionJobsSection from "./StudentSubmissionJobs";
import BulkUploadSubmissionJobs from "./BulkUploadSubmissionJobs";

export const AssignmentGroupSchoolsCard = ({ assignmentGroup }) => {
  const navigate = useNavigate();

  const schools = useMemo(() => {
    const grouped = {};

    (assignmentGroup?.assignments || []).forEach((assignment) => {
      const schoolName =
        assignment.schoolClass?.school?.name || "Unknown School";

      if (!grouped[schoolName]) {
        grouped[schoolName] = [];
      }

      grouped[schoolName].push(assignment);
    });

    return Object.entries(grouped)
      .map(([schoolName, assignments]) => ({
        schoolName,
        assignments,
      }))
      .sort((a, b) => a.schoolName.localeCompare(b.schoolName));
  }, [assignmentGroup]);

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Schools
        </Typography>

        <Stack spacing={2}>
          {schools.map((school) => (
            <Accordion key={school.schoolName}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    pr: 2,
                  }}
                >
                  <Typography variant="subtitle1">
                    {school.schoolName}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    {school.assignments.length} class
                    {school.assignments.length !== 1 ? "es" : ""}
                  </Typography>
                </Box>
              </AccordionSummary>

              <AccordionDetails>
                <Stack spacing={2}>
                  {school.assignments.map((assignment) => (
                    <Box
                      key={assignment.id}
                      sx={{
                        p: 2,
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={2}
                      >
                        <Box flex={1}>
                          <Typography variant="subtitle2">
                            {assignment.schoolClass?.name}
                          </Typography>

                          <Typography variant="body2" color="text.secondary">
                            {assignment.name}
                          </Typography>

                          <Box sx={{ mt: 1 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {assignment.percentageComplete ?? 0}% complete
                            </Typography>

                            <LinearProgress
                              variant="determinate"
                              value={assignment.percentageComplete ?? 0}
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                        </Box>

                        <Button
                          variant="contained"
                          onClick={() =>
                            navigate(
                              `/assignments/submission-hub/${assignment.id}?groupId=${assignmentGroup.id}`,
                            )
                          }
                        >
                          Open
                        </Button>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};

const AssignmentSubmissionHubContent = ({ assignmentId }) => {
  const ApiClient = useApiClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const groupId = searchParams.get("groupId");

  const [assignment, setAssignment] = useState(null);

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [multiStudentUploadDialogOpen, setMultiStudentUploadDialogOpen] =
    useState(false);

  const [prefillIdentifier, setPrefillIdentifier] = useState(null);

  const [selectedQuestionId, setSelectedQuestionId] = useState(null);

  const [assignmentQuestion, setAssignmentQuestion] = useState(null);

  const [answersAndIdentifiers, setAnswersAndIdentifiers] = useState({});

  const [jobsRefreshKey, setJobsRefreshKey] = useState(0);

  useEffect(() => {
    const fetchAssignment = async () => {
      try {
        const res = await ApiClient.assignment.getAssignment({
          assignment: assignmentId,
        });

        if (res) {
          setAssignment(res.assignment);
        }
      } catch (e) {
        console.error("Error fetching assignment:", e);
      }
    };

    if (assignmentId) {
      fetchAssignment();
    }
  }, [assignmentId]);

  useEffect(() => {
    if (assignment?.assignmentQuestions?.length > 0 && !selectedQuestionId) {
      setSelectedQuestionId(assignment.assignmentQuestions[0].id);
    }
  }, [assignment, selectedQuestionId]);

  useEffect(() => {
    const fetchAssignmentQuestion = async () => {
      if (!selectedQuestionId) {
        return;
      }

      try {
        const response = await ApiClient.assignment.getAssignmentQuestion({
          assignmentQuestion: selectedQuestionId,
        });

        if (response) {
          setAssignmentQuestion(response.assignmentQuestion);
        }
      } catch (error) {
        console.error("Failed to fetch assignment question", error);
      }
    };

    fetchAssignmentQuestion();
  }, [selectedQuestionId]);

  useEffect(() => {
    if (!selectedQuestionId) {
      return;
    }

    const subscription = ApiClient.assignment.pollAnswersAndIdentifiers({
      assignmentQuestion: selectedQuestionId,
      pollInterval: 5000,
      onUpdate: (data) => setAnswersAndIdentifiers(data),
    });

    return () => subscription.unsubscribe();
  }, [selectedQuestionId]);

  const handleQuestionChange = (event) => {
    setSelectedQuestionId(event.target.value);
  };

  const handleJobCreated = () => {
    setJobsRefreshKey((key) => key + 1);
    setPrefillIdentifier(null);
  };

  return (
    <>
      {assignment && (
        <>
          <AssignmentUploadDialog
            open={uploadDialogOpen}
            setOpen={setUploadDialogOpen}
            assignment={assignment}
            bulkUploadMode
            onJobCreated={handleJobCreated}
            prefillIdentifier={prefillIdentifier}
          />

          <AssignmentUploadDialog
            open={multiStudentUploadDialogOpen}
            setOpen={setMultiStudentUploadDialogOpen}
            assignment={assignment}
            bulkUploadMode
            multiStudentMode
            onJobCreated={handleJobCreated}
          />
        </>
      )}

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h3">Submission Hub</Typography>
            </Stack>

            <Grid container spacing={2} direction="row" sx={{ mt: 2, mb: 2 }}>
              <Grid size={3}>
                <InformationSnippet
                  title="Class"
                  value={assignment?.schoolClass?.name || "N/A"}
                />
              </Grid>

              <Grid size={3}>
                <InformationSnippet
                  title="Subject"
                  value={assignment?.schoolClass?.subject?.name || "N/A"}
                />
              </Grid>

              <Grid size={3}>
                <InformationSnippet
                  title="Due Date"
                  value={
                    assignment?.dueDate ? formatDate(assignment.dueDate) : "N/A"
                  }
                />
              </Grid>

              <Grid size={3}>
                <InformationSnippet
                  title="Assignment"
                  value={assignment?.name || "N/A"}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Button
          variant="card-action"
          startIcon={<ChevronLeft />}
          sx={{ mb: 2, background: "transparent" }}
          onClick={() =>
            navigate(
              groupId
                ? `/assignments/submission-hub/group/${groupId}`
                : `/assignments/view/${assignmentId}`,
            )
          }
        >
          {groupId ? "Back to main submission hub" : "Back to assignment"}
        </Button>

        {assignmentQuestion && (
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Stack direction="column" spacing={3}>
                <Stack
                  direction={{
                    xs: "column",
                    md: "row",
                  }}
                  spacing={2}
                  alignItems={{
                    xs: "stretch",
                    md: "center",
                  }}
                  justifyContent="space-between"
                >
                  {assignment?.assignmentQuestions?.length > 1 ? (
                    <FormControl sx={{ minWidth: 200 }}>
                      <InputLabel id="question-select-label">
                        Question
                      </InputLabel>

                      <Select
                        labelId="question-select-label"
                        value={selectedQuestionId || ""}
                        label="Question"
                        onChange={handleQuestionChange}
                      >
                        {assignment.assignmentQuestions.map((question) => (
                          <MenuItem key={question.id} value={question.id}>
                            Question {question.identifier}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <Box />
                  )}

                  <Stack direction="row" spacing={2}>
                    <Tooltip title="Upload a document containing multiple answers of a single student">
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => setUploadDialogOpen(true)}
                        data-testid={"student-booklet-upload-button"}
                      >
                        Upload Student Booklet
                      </Button>
                    </Tooltip>

                    <Tooltip title="Upload multiple students' answers (separated by cover sheets)">
                      <Button
                        variant="outlined"
                        startIcon={<GroupAddIcon />}
                        onClick={() => setMultiStudentUploadDialogOpen(true)}
                        data-testid={"multi-student-upload-button"}
                      >
                        Multi-student Upload
                      </Button>
                    </Tooltip>
                  </Stack>
                </Stack>

                <SubmissionSection
                  assignmentQuestion={assignmentQuestion}
                  identifiers={answersAndIdentifiers?.existingStudents}
                  answers={answersAndIdentifiers?.answers}
                />
              </Stack>
            </CardContent>
          </Card>
        )}
        <BulkUploadSubmissionJobs
          assignmentId={assignmentId}
          refreshKey={jobsRefreshKey}
        />

        <StudentSubmissionJobsSection
          assignmentId={assignmentId}
          refreshKey={jobsRefreshKey}
        />
      </Container>
    </>
  );
};

const AssignmentGroupSubmissionHubContent = ({ assignmentGroupId }) => {
  const ApiClient = useApiClient();
  const navigate = useNavigate();

  const [assignmentGroup, setAssignmentGroup] = useState(null);

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  const [jobsRefreshKey, setJobsRefreshKey] = useState(0);

  useEffect(() => {
    const fetchAssignmentGroup = async () => {
      try {
        const res = await ApiClient.assignment.getAssignmentGroup({
          assignmentGroup: assignmentGroupId,
        });

        if (res) {
          setAssignmentGroup(res.assignmentGroup);
        }
      } catch (e) {
        console.error("Error fetching assignment group:", e);
      }
    };

    if (assignmentGroupId) {
      fetchAssignmentGroup();
    }
  }, [assignmentGroupId]);

  const handleUploadSuccess = () => {
    setJobsRefreshKey((key) => key + 1);
  };

  return (
    <>
      {assignmentGroup && (
        <AssignmentGroupUploadDialog
          open={uploadDialogOpen}
          setOpen={setUploadDialogOpen}
          assignmentGroupId={assignmentGroupId}
          onSuccess={handleUploadSuccess}
        />
      )}

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h3">Submission Hub</Typography>
            </Stack>

            <Grid container spacing={2} direction="row" sx={{ mt: 2, mb: 2 }}>
              <Grid size={3}>
                <InformationSnippet
                  title="Name"
                  value={assignmentGroup?.name || "N/A"}
                />
              </Grid>

              <Grid size={3}>
                <InformationSnippet
                  title="Subject"
                  value={assignmentGroup?.subject?.name || "N/A"}
                />
              </Grid>

              <Grid size={3}>
                <InformationSnippet
                  title="Level"
                  value={assignmentGroup?.educationLevel?.level || "N/A"}
                />
              </Grid>

              <Grid size={3}>
                <InformationSnippet
                  title="Classes"
                  value={assignmentGroup?.assignments?.length ?? "N/A"}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Stack
          direction="row"
          spacing={2}
          sx={{
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Button
            variant="card-action"
            startIcon={<ChevronLeft />}
            sx={{ mb: 2, background: "transparent" }}
            onClick={() => navigate(`/assignments/groups/${assignmentGroupId}`)}
          >
            Back to assignment group
          </Button>

          <Tooltip title="Upload multiple students' answers (separated by cover sheets)">
            <Button
              variant="outlined"
              startIcon={<GroupAddIcon />}
              onClick={() => setUploadDialogOpen(true)}
              data-testid={"multi-student-upload-button-assignment-group"}
            >
              Multi-student Upload
            </Button>
          </Tooltip>
        </Stack>

        <AssignmentGroupSchoolsCard assignmentGroup={assignmentGroup} />

        <BulkUploadSubmissionJobs
          assignmentGroupId={assignmentGroupId}
          refreshKey={jobsRefreshKey}
        />
        <StudentSubmissionJobsSection
          assignmentGroupId={assignmentGroupId}
          refreshKey={jobsRefreshKey}
        />
      </Container>
    </>
  );
};

const SubmissionHubContent = () => {
  const { assignmentId, assignmentGroupId } = useParams();

  if (assignmentId) {
    return <AssignmentSubmissionHubContent assignmentId={assignmentId} />;
  }

  if (assignmentGroupId) {
    return (
      <AssignmentGroupSubmissionHubContent
        assignmentGroupId={assignmentGroupId}
      />
    );
  }

  throw new Error("Invalid assignment or assignment group");
};

export const SubmissionHub = () => {
  return <SubmissionHubContent />;
};

export default withPageTracking(SubmissionHub, {
  feature: "submission-hub",
});
